<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    OnboardingPackagesService,
    type CustomerAdminOnboardingPackage as OnboardingPackage
  } from '@/types/generated'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { genericErrorToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import {
    Pencil,
    CheckCircle2,
    X,
    Link2,
    Trash,
    PlusIcon,
    QrCode,
    Package,
    TextCursorInput
  } from 'lucide-vue-next'
  import QRCodeDialog from '@/components/admin/QRCodeDialog.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const { t } = useTranslation({
    en: {
      newOnboardingPackage: 'New On Site Client Intake',
      defaultPackageName: 'New On Site Client Intake',
      name: 'Name',
      lawFieldTopic: 'Law field/Topic',
      updatedBy: 'Updated by',
      updatedOn: 'Updated on',
      lawField: 'Law field',
      allLawFields: 'All Law Fields',
      lawTopic: 'Law topic',
      allLawTopics: 'All Law Topics',
      copyLink: 'Copy reusable link',
      edit: 'Edit',
      rename: 'Rename',
      delete: 'Delete',
      confirmDeletion: 'Confirm Deletion',
      areYouSureDelete: 'Are you sure you want to delete the On Site Client Intake',
      actionCannotBeUndone: 'This action cannot be undone.',
      cancel: 'Cancel',
      linkCopiedSuccessfully: 'Link copied successfully to clipboard!',
      qrCode: 'QR Code',
      qrCodeScanDescription: 'Scan this QR code to access the On Site Client Intake',
      copyLinkButton: 'Copy link',
      qrCodeSave: 'Save QR Code',
      linkCopied: 'Link copied',
      linkCopiedDescription: 'The link has been copied to the clipboard',
      error: 'Error',
      linkCopyError: 'The link could not be copied',
      justNow: 'just now',
      oneMinuteAgo: '1 minute ago',
      minutesAgo: 'minutes ago',
      oneHourAgo: '1 hour ago',
      hoursAgo: 'hours ago',
      oneDayAgo: '1 day ago',
      daysAgo: 'days ago',
      oneWeekAgo: '1 week ago',
      weeksAgo: 'weeks ago',
      oneMonthAgo: '1 month ago',
      monthsAgo: 'months ago',
      oneYearAgo: '1 year ago',
      yearsAgo: 'years ago',
      ago: 'ago',
      sortBy: 'Sort by',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      recentlyModified: 'Recently modified',
      leastRecentlyModified: 'Least recently modified',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    },
    de: {
      newOnboardingPackage: 'Neue Vor-Ort Mandantenaufnahme',
      defaultPackageName: 'Neue Vor-Ort Mandantenaufnahme',
      name: 'Name',
      lawFieldTopic: 'Rechtsgebiet/Thema',
      updatedBy: 'Aktualisiert von',
      updatedOn: 'Aktualisiert am',
      lawField: 'Rechtsgebiet',
      allLawFields: 'Alle Rechtsgebiete',
      lawTopic: 'Rechtsthema',
      allLawTopics: 'Alle Rechtsthemen',
      copyLink: 'Wiederverwendbaren Link kopieren',
      edit: 'Bearbeiten',
      rename: 'Umbenennen',
      delete: 'Löschen',
      confirmDeletion: 'Löschung bestätigen',
      areYouSureDelete: 'Sind Sie sicher, dass Sie die Vor-Ort Mandantenaufnahme löschen möchten',
      actionCannotBeUndone: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      linkCopiedSuccessfully: 'Link erfolgreich in die Zwischenablage kopiert!',
      qrCode: 'QR Code',
      qrCodeScanDescription:
        'Scannen Sie diesen QR Code, um auf die Vor-Ort Mandantenaufnahme zuzugreifen',
      copyLinkButton: 'Link kopieren',
      qrCodeSave: 'QR Code speichern',
      linkCopied: 'Link kopiert',
      linkCopiedDescription: 'Der Link wurde in die Zwischenablage kopiert',
      error: 'Fehler',
      linkCopyError: 'Der Link konnte nicht kopiert werden',
      justNow: 'gerade eben',
      oneMinuteAgo: 'vor 1 Minute',
      minutesAgo: 'Minuten',
      oneHourAgo: 'vor 1 Stunde',
      hoursAgo: 'Stunden',
      oneDayAgo: 'vor 1 Tag',
      daysAgo: 'Tagen',
      oneWeekAgo: 'vor 1 Woche',
      weeksAgo: 'Wochen',
      oneMonthAgo: 'vor 1 Monat',
      monthsAgo: 'Monaten',
      oneYearAgo: 'vor 1 Jahr',
      yearsAgo: 'Jahren',
      ago: 'vor',
      sortBy: 'Sortieren nach',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      recentlyModified: 'Kürzlich geändert',
      leastRecentlyModified: 'Am wenigsten geändert',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    }
  })

  const router = useRouter()
  const { toast } = useToast()

  const showDeleteModal = ref(false)
  const packageToDelete = ref<OnboardingPackage | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const editingPackage = ref<string | null>(null)
  const newName = ref('')
  const showQRCodeModal = ref(false)
  const qrCodeUrl = ref('')
  const qrCodeTitle = ref('')

  const { fetchLawFields } = useLawFieldStore()
  const { translateLawTopic, lawTopicTranslations } = useLawTopicTranslation()
  const { translateLawField, lawFieldTranslations } = useLawFieldTranslation()

  const lawFields = computed(() => useLawFieldStore().lawFields)

  const allLawFieldOptions = computed(() => {
    if (!lawFields.value?.data) {
      return []
    }

    // Access lawFieldTranslations to ensure reactivity to locale changes
    const translations = lawFieldTranslations.value

    const options = Object.keys(lawFields.value.data)
      .map((field) => ({
        value: field,
        label: translations[field] || field
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return options
  })

  const allLawTopicOptions = computed(() => {
    if (!lawFields.value?.data) {
      return []
    }

    // Access lawTopicTranslations to ensure reactivity to locale changes
    const translations = lawTopicTranslations.value

    const allTopics = new Set<string>()
    Object.values(lawFields.value.data).forEach((topics) => {
      topics.forEach((topic) => allTopics.add(topic))
    })
    const options = Array.from(allTopics)
      .map((topic) => ({
        value: topic,
        label: translations[topic] || topic
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return options
  })

  const fetchOnboardingPackages = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const lawFieldFilter = filters.find((filter) => filter.key === 'law_field')?.value
    const lawTopicFilter = filters.find((filter) => filter.key === 'law_topic')?.value
    const orderingFilter = filters.find((filter) => filter.key === 'ordering')?.value

    const queryParams: any = {
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      ordering: orderingFilter || '-updated_at'
    }

    if (lawFieldFilter && lawFieldFilter !== 'all') {
      queryParams.lawField = lawFieldFilter
    }

    if (lawTopicFilter && lawTopicFilter !== 'all') {
      queryParams.lawTopic = lawTopicFilter
    }

    const response = await OnboardingPackagesService.onboardingPackagesList(queryParams)

    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count ?? 0
    }
  }

  const filterableFields = computed<Filter[]>(() => {
    const fields = [
      {
        key: 'ordering',
        label: t('sortBy'),
        type: 'select' as const,
        value: '-updated_at',
        options: [
          { value: '-updated_at', label: t('recentlyModified') },
          { value: 'updated_at', label: t('leastRecentlyModified') },
          { value: '-created_at', label: t('newestFirst') },
          { value: 'created_at', label: t('oldestFirst') },
          { value: 'name', label: t('nameAZ') },
          { value: '-name', label: t('nameZA') }
        ]
      },
      {
        key: 'law_field',
        label: t('lawField'),
        type: 'select' as const,
        options: [{ value: 'all', label: t('allLawFields') }, ...allLawFieldOptions.value]
      },
      {
        key: 'law_topic',
        label: t('lawTopic'),
        type: 'select' as const,
        options: [{ value: 'all', label: t('allLawTopics') }, ...allLawTopicOptions.value]
      }
    ]
    return fields
  })

  const createOnboardingPackage = async () => {
    try {
      // Create the package immediately with a default name
      const created = await OnboardingPackagesService.onboardingPackagesCreate({
        requestBody: {
          name: t('defaultPackageName'),
          form_uuids: [],
          questionnaire_uuids: []
        }
      })

      // Navigate to the newly created package
      router.push(
        ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGE_DETAIL.replace(
          ':uuid',
          (created as any).uuid
        )
      )
    } catch (error) {
      genericErrorToast()
    }
  }

  const openDeleteModal = (pkg: OnboardingPackage) => {
    packageToDelete.value = pkg
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    packageToDelete.value = null
  }

  const confirmDelete = async () => {
    if (packageToDelete.value) {
      try {
        await OnboardingPackagesService.onboardingPackagesDestroy({
          uuid: packageToDelete.value.uuid
        })
        closeDeleteModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const startEditing = (pkg: OnboardingPackage) => {
    editingPackage.value = pkg.uuid
    newName.value = pkg.name
  }

  const cancelEditing = () => {
    editingPackage.value = null
    newName.value = ''
  }

  const saveNewName = async (pkg: OnboardingPackage) => {
    try {
      const updatedPackage = {
        ...pkg,
        name: newName.value
      }

      await OnboardingPackagesService.onboardingPackagesUpdate({
        uuid: pkg.uuid,
        requestBody: updatedPackage
      })

      editingPackage.value = null
      newName.value = ''

      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error updating onboarding package:', error)
      genericErrorToast()
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      const fullUrl = `${window.location.origin}${text}`
      await navigator.clipboard.writeText(fullUrl)
      toast({
        title: t('linkCopied'),
        description: t('linkCopiedDescription'),
        variant: 'default'
      })
    } catch (err) {
      console.error('Async: Could not copy text: ', err)
      try {
        const textArea = document.createElement('textarea')
        textArea.value = `${window.location.origin}${text}`
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast({
          title: t('linkCopied'),
          description: t('linkCopiedDescription'),
          variant: 'default'
        })
      } catch (fallbackErr) {
        console.error('Fallback: Could not copy text: ', fallbackErr)
        toast({
          variant: 'destructive',
          title: t('error'),
          description: t('linkCopyError')
        })
      }
    }
  }

  const openQRCodeModal = (pkg: OnboardingPackage) => {
    const clientPortalOnboarding = `${ROUTES.CLIENT_PORTAL.HOME}/onboarding/:uuid`
    const packageUrl = clientPortalOnboarding.replace(':uuid', pkg.uuid)
    qrCodeUrl.value = `${window.location.origin}${packageUrl}?qr=true`
    qrCodeTitle.value = `${t('qrCode')} - ${pkg.name}`
    showQRCodeModal.value = true
  }

  const getCreatorInitials = (name: string | undefined): string => {
    if (!name) return '?'

    const words = name.trim().split(' ')
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }

    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  }

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return t('justNow')
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? t('oneMinuteAgo') : `${diffInMinutes} ${t('minutesAgo')}`
    }

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return diffInHours === 1 ? t('oneHourAgo') : `${diffInHours} ${t('hoursAgo')}`
    }

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) {
      return diffInDays === 1 ? t('oneDayAgo') : `${diffInDays} ${t('daysAgo')}`
    }

    const diffInWeeks = Math.floor(diffInDays / 7)
    const diffInMonths = Math.floor(diffInDays / 30)

    if (diffInMonths === 0) {
      return diffInWeeks === 1 ? t('oneWeekAgo') : `${diffInWeeks} ${t('weeksAgo')}`
    }

    if (diffInMonths < 12) {
      return diffInMonths === 1 ? t('oneMonthAgo') : `${diffInMonths} ${t('monthsAgo')}`
    }

    const diffInYears = Math.floor(diffInDays / 365)
    return diffInYears === 1 ? t('oneYearAgo') : `${diffInYears} ${t('yearsAgo')}`
  }

  onMounted(async () => {
    if (!lawFields.value) {
      await fetchLawFields()
    }
  })
</script>

<template>
  <SettingsPage>
    <template #actions>
      <Button
        data-cy="new-onboarding-package-button"
        variant="default"
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
        @click="createOnboardingPackage"
      >
        <PlusIcon class="h-4 w-4" />
        {{ t('newOnboardingPackage') }}
      </Button>
    </template>

    <div class="space-y-4">
      <PaginatedTableView
        ref="paginatedTableRef"
        searchable-fields="name"
        :fetch-data="fetchOnboardingPackages"
        :filterable-fields="filterableFields"
        class="onboarding-package-table"
      >
        <template #table-head>
          <TableCell class="min-w-[250px] max-w-[350px]">{{ t('name') }}</TableCell>
          <TableCell class="w-[180px]">{{ t('lawFieldTopic') }}</TableCell>
          <TableCell class="w-[150px]">{{ t('updatedBy') }}</TableCell>
          <TableCell class="w-[130px]">{{ t('updatedOn') }}</TableCell>
          <TableCell class="w-[60px]"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="pkg in items"
            :key="pkg.uuid"
            :data-cy="`onboarding-package-row-${pkg.uuid}`"
            class="group hover:bg-gray-50"
          >
            <!-- Name column with inline editing -->
            <TableCell class="min-w-[250px] max-w-[350px] py-4">
              <div
                v-if="editingPackage === pkg.uuid"
                class="flex items-center gap-3"
              >
                <div class="relative flex-1">
                  <input
                    v-model="newName"
                    type="text"
                    class="w-full rounded-md border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-green-600 hover:text-green-700"
                  @click="saveNewName(pkg)"
                >
                  <CheckCircle2 class="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-red-600 hover:text-red-700"
                  @click="cancelEditing"
                >
                  <X class="h-4 w-4" />
                </Button>
              </div>
              <div
                v-else
                class="flex min-w-0 items-center justify-between"
              >
                <div class="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    :data-cy="`package-name-${pkg.uuid}`"
                    class="cursor-pointer truncate font-medium text-gray-900 transition-colors hover:text-purple-600
                      hover:underline"
                    :title="pkg.name"
                    @click="
                      $router.push(
                        ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGE_DETAIL.replace(
                          ':uuid',
                          pkg.uuid
                        )
                      )
                    "
                  >
                    {{ pkg.name }}
                  </span>
                </div>
              </div>
            </TableCell>

            <!-- Law field/Topic column -->
            <TableCell class="w-[180px] py-4">
              <div class="space-y-1 overflow-hidden">
                <div
                  class="truncate text-sm font-medium text-gray-900"
                  :title="translateLawField(pkg.law_field) || '-'"
                >
                  {{ translateLawField(pkg.law_field) || '-' }}
                </div>
                <div
                  class="truncate text-sm text-gray-600"
                  :title="translateLawTopic(pkg.law_topic) || '-'"
                >
                  {{ translateLawTopic(pkg.law_topic) || '-' }}
                </div>
              </div>
            </TableCell>

            <!-- Updated by column -->
            <TableCell class="w-[150px] py-4">
              <div class="flex min-w-0 items-center gap-2 overflow-hidden">
                <div
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-medium
                    text-white"
                >
                  {{ getCreatorInitials(pkg.created_by_name) }}
                </div>
                <span class="truncate text-gray-900">
                  {{ pkg.created_by_name || '-' }}
                </span>
              </div>
            </TableCell>

            <!-- Updated on column -->
            <TableCell class="w-[130px] py-4">
              <span class="whitespace-nowrap text-gray-600">
                {{ formatRelativeTime(pkg.updated_at) }}
              </span>
            </TableCell>

            <!-- Actions column -->
            <TableCell class="w-[60px] py-4">
              <TableOptionsButton
                :dataCy="`package-options-button-${pkg.uuid}`"
                :options="[
                  {
                    label: t('edit'),
                    dataCy: `package-edit-${pkg.uuid}`,
                    to: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGE_DETAIL.replace(
                      ':uuid',
                      pkg.uuid
                    ),
                    icon: Pencil
                  },
                  {
                    label: t('rename'),
                    dataCy: `package-rename-${pkg.uuid}`,
                    onClick: () => startEditing(pkg),
                    icon: TextCursorInput
                  },
                  {
                    label: t('copyLink'),
                    dataCy: `package-copy-link-${pkg.uuid}`,
                    onClick: () => {
                      const clientPortalOnboarding = `${ROUTES.CLIENT_PORTAL.HOME}/onboarding/:uuid`
                      const packageUrl = clientPortalOnboarding.replace(':uuid', pkg.uuid)
                      copyToClipboard(packageUrl)
                    },
                    icon: Link2
                  },
                  {
                    label: t('qrCode'),
                    dataCy: `package-qr-code-${pkg.uuid}`,
                    icon: QrCode,
                    onClick: () => openQRCodeModal(pkg)
                  },
                  {
                    label: t('delete'),
                    dataCy: `package-delete-${pkg.uuid}`,
                    onClick: () => openDeleteModal(pkg),
                    icon: Trash
                  }
                ]"
              />
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent data-cy="delete-package-modal">
      <DialogHeader>
        <DialogTitle>{{ t('confirmDeletion') }}</DialogTitle>
        <DialogDescription>
          {{ t('areYouSureDelete') }} "{{ packageToDelete?.name }}"?
          {{ t('actionCannotBeUndone') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          data-cy="delete-cancel-button"
          variant="secondary"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          data-cy="delete-confirm-button"
          variant="destructive"
          @click="confirmDelete"
        >
          {{ t('delete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- QR Code Modal -->
  <QRCodeDialog
    v-model="showQRCodeModal"
    :url="qrCodeUrl"
    :title="qrCodeTitle"
    :description="t('qrCodeScanDescription')"
    :copy-button-text="t('copyLinkButton')"
    :save-button-text="t('qrCodeSave')"
    :copied-title="t('linkCopied')"
    :copied-description="t('linkCopiedDescription')"
    :copy-error-title="t('error')"
    :copy-error-description="t('linkCopyError')"
  />
</template>

<style scoped>
  :deep(.onboarding-package-table table) {
    table-layout: fixed;
    width: 100%;
  }
</style>
